
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ResetPasswordDialog } from "@/components/ResetPasswordDialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RequirePasswordProps {
  email?: string;
  onComplete: () => void;
}

export const RequirePassword = ({ email, onComplete }: RequirePasswordProps) => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if we're in a passwordless session (e.g., magic link login)
    const checkAuthMethod = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log("RequirePassword check:", {
          provider: session?.user?.app_metadata?.provider,
          hasSetPassword: session?.user?.user_metadata?.has_set_password,
          email: session?.user?.email
        });
        
        // Check for the auth provider - if it's 'email' (magic link) with no password set
        if (session?.user?.app_metadata?.provider === 'email' && 
            !session?.user?.user_metadata?.has_set_password) {
          setShowPasswordDialog(true);
        } else if (session?.user?.user_metadata?.has_set_password) {
          // If user already has a password set, skip this screen completely
          console.log("User already has password set, skipping password prompt");
          onComplete();
        }
      } catch (error) {
        console.error("Error checking auth method:", error);
      }
    };

    checkAuthMethod();
  }, [onComplete]);

  const handleSkip = async () => {
    try {
      // Mark that user has seen this dialog even if they skipped
      await supabase.auth.updateUser({
        data: { has_seen_password_prompt: true }
      });
      
      toast({
        title: "Continuing without password",
        description: "You can set a password later in your account settings.",
      });
      
      onComplete();
    } catch (error) {
      console.error("Error skipping password setup:", error);
    }
  };

  return (
    <>
      {showPasswordDialog ? (
        <ResetPasswordDialog 
          open={showPasswordDialog} 
          onOpenChange={setShowPasswordDialog}
          onSuccess={onComplete}
        />
      ) : (
        <Card className="w-full max-w-md mx-auto mt-8">
          <CardHeader>
            <CardTitle>Set up your account</CardTitle>
            <CardDescription>
              You've been invited to view training resources. Create a password to secure your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Creating a password will allow you to log in directly in the future.
            </p>
            <div className="flex flex-col space-y-2">
              <Button 
                onClick={() => setShowPasswordDialog(true)}
                className="w-full"
              >
                Create Password
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSkip}
                className="w-full"
              >
                Skip for Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
